import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { IArticle, IArticleImage, IAuthor } from '@curbnturf/entities';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'article';

export interface IArticleSeo {
  excerpt: string;
  shortDescription: string;
  metaDescription: string;
  imageAltText: string;
}

/**
 * Articles, article images, categories and authors (writes are
 * AdminAuthGuard-ed on the server). Replaces @curbnturf/article's
 * ArticleService, which sat on @curbnturf/network's CachedHttpClient, and its
 * NgRx ArticleFacade.
 */
@Injectable({ providedIn: 'root' })
export class AdminArticleApiService {
  private readonly http = inject(HttpClient);

  list(): Observable<IArticle[]> {
    return this.http.get<IArticle[]>(API_URL);
  }

  create(article: IArticle): Observable<IArticle> {
    return this.http.post<IArticle>(API_URL, article);
  }

  update(article: IArticle): Observable<IArticle> {
    if (!article.id) {
      throw new Error('Missing Article Identifier');
    }
    return this.http.put<IArticle>(`${API_URL}/${article.id}`, article);
  }

  remove(articleId: number): Observable<IArticle> {
    return this.http.delete<IArticle>(`${API_URL}/${articleId}`);
  }

  /** Server-side (Anthropic proxy) SEO copy from an article's title + body. */
  generateSeo(input: { title: string; body: string; categories: string[] }): Observable<IArticleSeo> {
    return this.http.post<IArticleSeo>(`${API_URL}/seo`, input);
  }

  listImages(): Observable<IArticleImage[]> {
    return this.http.get<IArticleImage[]>(`${API_URL}/image`);
  }

  uploadImage(formData: FormData): Observable<IArticleImage> {
    return this.http.post<IArticleImage>(`${API_URL}/image`, formData);
  }

  removeImage(imageId: number): Observable<IArticleImage> {
    return this.http.delete<IArticleImage>(`${API_URL}/image/${imageId}`);
  }

  listCategories(): Observable<string[]> {
    return this.http.get<string[]>(`${API_URL}/category`);
  }

  listAuthors(): Observable<IAuthor[]> {
    return this.http.get<IAuthor[]>(`${API_URL}/author`);
  }

  createAuthor(author: IAuthor): Observable<IAuthor> {
    return this.http.post<IAuthor>(`${API_URL}/author`, author);
  }

  updateAuthor(author: IAuthor): Observable<IAuthor> {
    if (!author.id) {
      throw new Error('Missing Author Identifier');
    }
    return this.http.put<IAuthor>(`${API_URL}/author/${author.id}`, author);
  }

  removeAuthor(authorId: number): Observable<IAuthor> {
    return this.http.delete<IAuthor>(`${API_URL}/author/${authorId}`);
  }
}
